'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { isStaleDeploymentError } from '@/lib/utils/stale_deployment';

const STALE_DEPLOYMENT_TOAST_ID = 'stale-deployment';

// Offers a reload when this tab calls a Server Action the current deployment no longer has.
// Listens globally, so query, mutation, and directly awaited action failures all end up here;
// the fixed toast id collapses a burst of failures into one notice.
export function StaleDeploymentNotice() {
  const t = useTranslations('StaleDeploymentNotice');
  const queryClient = useQueryClient();

  useEffect(() => {
    const notify = (error: unknown) => {
      if (!isStaleDeploymentError(error)) return;

      toast(t('title'), {
        id: STALE_DEPLOYMENT_TOAST_ID,
        description: t('description'),
        duration: Infinity,
        action: {
          label: t('reload'),
          onClick: () => window.location.reload(),
        },
      });
    };

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') notify(event.action.error);
    });

    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') notify(event.action.error);
    });

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => notify(event.reason);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [queryClient, t]);

  return null;
}
